import React, { memo, ReactNode } from 'react';

import Tooltip from '@plasma/ui.display.tooltip';
import PopConfirm from '@plasma/ui.feedback.pop-confirm';
import Progress from '@plasma/ui.feedback.progress';
import Button from '@plasma/ui.general.button';
import Input from '@plasma/ui.input.input';
import Select from '@plasma/ui.input.select';
import Modal from '@plasma/ui.layout.modal';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';

import EditableTable from '../../components/edit-table';
import LoadingComponent from '../../components/Loding-component/LoadingComponent';
import TopBarThemed from '../../components/top-bar-themed';
import consumableLifeCycleColumns from '../../types/consumableLifetime&CycleDataType';
import useConsumableLifetimeCycleHook from './consumable-lifetime-cycle.hook';
import { NormalPlasmaButton, NormalPlasmaSelect } from './consumable-lifetime-cycle.styles';
import * as styled from './consumable-lifetime-cycle.styles';
import themes from './themes';

const { Option } = Select;
export interface ConsumableLifetimeCycleProps {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type Props = ConsumableLifetimeCycleProps & ThemeOverride;

function ConsumableLifetimeCycle(props: Props) {
  const { children, className, style, size, themeMode } = props;

  const {
    t,
    dataForTable,
    currentDataForTable,
    pageNumber,
    totalPage,
    isLoading,
    isDrawingLoading,
    showConsumableCodeModal,
    selectedCode,
    selectedEquipment,
    selectedEquipmentId,
    iFrameSrc,
    showConsumableCodeDrawing,
    clearSelection,
    searchLogoParameter,
    searchLogoParamOptions,
    lifetimeInput,
    lifetimeInputLast,
    waitingForExcelFile,
    searchVisible,
    editlifeandcycle,
    globalLife,
    globalCycle,
    dataForTableCopy,
    showUploadAllConsumablesModal,
    totalFileCount,
    processedFileCount,
    progressPercentage,
    handleLifeCycleEditModalCSave,
    setGlobalCycle,
    setGlobalLife,
    handleLifeCycleEditModalClose,
    handleToggleSearch,
    handleNextPage,
    handlePreviousPage,
    handleFirstPage,
    handleLastPage,
    handleSearch,
    handleFilterByLifetime,
    handleSearchLogoParamOptions,
    handleLifetimeInputChange,
    handleLifetimeInputLastChange,
    handleSaveDeleteFromTable,
    handleSelection,
    handleModalCancel,
    handleSaveFromModal,
    handleFileUpload,
    handleDeletePDF,
    handleDrawingModalCancel,
    downloadExcelFile,
    handleUploadConsumableDrawingsModalCancel,
    handleFolderSelection,
    userRoles,
    isAdmin,
  } = useConsumableLifetimeCycleHook(props);

  return (
    <ThemeProvider themes={themes} selectedSize={size} selectedMode={themeMode}>
      <styled.ConsumableLifetimeCycle
        className={`consumable-lifetime-cycle ${className ?? ''}`.trim()}
        style={{ maxHeight: '84vh' }}
      >
        <TopBarThemed topHeading={t('consumable-lifetime-cycle')} />

        {isDrawingLoading && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
            }}
          >
            <LoadingComponent />
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginRight: '1.5vw',
            marginTop: '1.5vh',
            marginBottom: '1.5vh',
          }}
        >
          <div style={{ display: 'flex', gap: '3vw' }}>
            <NormalPlasmaButton
              type="primary"
              title="Download Excel"
              prefixIcon={{ name: 'download' }}
              onClick={downloadExcelFile}
              style={{ marginLeft: '2px' }}
              loading={waitingForExcelFile}
            />

            {!userRoles.includes('upload-folder-restricted') ? (
              <NormalPlasmaButton
                type="primary"
                title="Upload Consumable Drawings"
                prefixIcon={{ name: 'upload' }}
                onClick={handleFolderSelection}
              />
            ) : (
              <></>
            )}
          </div>

          <div style={{ display: 'flex' }}>
            <NormalPlasmaSelect
              style={{ width: 'fit-content', minWidth: '5vw' }}
              dropdownStyle={{ minWidth: '6.5vw' }}
              onChange={handleSearchLogoParamOptions}
              value={searchLogoParameter}
              allowClear
            >
              {searchLogoParamOptions?.map((item, index) => (
                <Option key={index} value={item.value}>
                  {item.display}
                </Option>
              ))}
            </NormalPlasmaSelect>

            <Input
              placeholder="Lifetime (t)"
              type="number"
              min={0}
              style={{ width: '7vw', marginLeft: '5px', marginRight: '5px' }}
              value={lifetimeInput}
              onChange={(data: any) => handleLifetimeInputChange(data)}
            />

            {searchLogoParameter === 'In Between' ? (
              <div>
                to
                <Input
                  placeholder="Lifetime (t)"
                  type="number"
                  min={0}
                  style={{ width: '7vw', marginLeft: '5px' }}
                  value={lifetimeInputLast}
                  onChange={(data: any) => handleLifetimeInputLastChange(data)}
                />
              </div>
            ) : (
              ''
            )}

            <NormalPlasmaButton
              type="primary"
              title="Find"
              prefixIcon={{ name: 'search' }}
              onClick={() => handleFilterByLifetime(lifetimeInput, lifetimeInputLast)}
              style={{ marginLeft: '10px' }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tooltip title="First Page" mouseEnterDelay={0.5}>
              <Button
                style={{ width: '1.5vw', height: '2vh', marginRight: '0.5vh' }}
                type="secondary"
                prefixIcon={{ name: 'first_page' }}
                onClick={handleFirstPage}
                disabled={pageNumber === 1}
              />
            </Tooltip>

            <Tooltip title="Previous Page" mouseEnterDelay={0.5}>
              <Button
                style={{ width: '1.3vw', height: '1.9vh', marginRight: '1vh' }}
                type="secondary"
                prefixIcon={{ name: 'navigate_before' }}
                disabled={pageNumber === 1}
                onClick={handlePreviousPage}
              />
            </Tooltip>

            <div
              style={{
                borderTop: '1px solid black',
                borderBottom: '1px solid black',
                borderRadius: '2px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '1.3vh',
                padding: '0 1vh 0 1vh',
              }}
            >
              {t('page')} {pageNumber} {t('of')} {totalPage}
            </div>

            <Tooltip title="Next Page" mouseEnterDelay={0.5}>
              <Button
                style={{ width: '1.3vw', height: '1.9vh', marginLeft: '1vh' }}
                type="secondary"
                prefixIcon={{ name: 'navigate_next' }}
                disabled={pageNumber === totalPage}
                onClick={handleNextPage}
              />
            </Tooltip>

            <Tooltip title="Last Page" mouseEnterDelay={0.5}>
              <Button
                style={{ width: '1.5vw', height: '2vh', marginLeft: '0.5vh' }}
                type="secondary"
                prefixIcon={{ name: 'last_page' }}
                onClick={handleLastPage}
                disabled={pageNumber === totalPage}
              />
            </Tooltip>
          </div>
        </div>

        {dataForTable && dataForTable.length && !isLoading ? (
          <EditableTable
            tableData={currentDataForTable}
            tableColumns={consumableLifeCycleColumns()}
            hiddenColumns={['cycles']}
            stillHiddenColumns={['cycles']}
            handleSave={handleSaveDeleteFromTable}
            showActions_ext
            allowSelection
            handleSelection={(rowIndex, rowData, event, column) => handleSelection(rowIndex, rowData, event, column)}
            selectInputReturnType={'value'}
            saveReturnType={'row'}
            onlyAddableFields={[
              'displayseq',
              'areacode',
              'zonecode',
              'equipmentcode',
              'consumabledescription',
              'consumablecode',
              'companyConsumableCode',
              'companyConsumableDesc',
            ]}
            clearTheSelection={clearSelection}
            showSerialNumbers
            ButtonsVisible={{
              Edit: userRoles.includes('allow_lifecycle_edit') || isAdmin != null ? true : false,
              MoveUp: false,
              MoveDown: false,
              InsertUp: false,
              InsertDown: false,
              Delete: false,
            }}
            showOnlyEditIcon={userRoles.includes('allow_lifecycle_edit') || isAdmin != null ? true : false}
            searchBarOptions={{
              handleSearch: (term, column) => handleSearch(term, column, dataForTableCopy),
              non_searchable_columns: [],
              showSearch: true,
              visible: searchVisible,
              toggleSearch: (e) => handleToggleSearch(e),
            }}
          />
        ) : (
          <LoadingComponent />
        )}

        <Modal
          title={
            isAdmin
              ? selectedCode.trim().length > 0
                ? `Upload Files for ${selectedCode}`
                : `Upload Files for ${selectedEquipment} (${selectedEquipmentId})`
              : selectedCode.trim().length > 0
                ? `Consumable Code: ${selectedCode}`
                : `${selectedEquipment} (${selectedEquipmentId})`
          }
          width={'30%'}
          okText={isAdmin ? t('save') : t('Ok')}
          cancelText={t('cancel')}
          visible={showConsumableCodeModal}
          onCancel={() => handleModalCancel()}
          onOk={() => handleSaveFromModal()}
          destroyOnClose
        >
          {isAdmin ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                background: 'linear-gradient(to top left, #f0f0f5, #33adff55)',
                padding: '15px 20px 20px 20px',
                borderRadius: '5px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  marginBottom: '2vh',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{
                    height: '3vh',
                    width: '7vw',
                    fontWeight: 'bold',
                    marginRight: '3vw',
                    fontSize: '15px',
                    borderBottom: '2px solid #75a3a3',
                    backgroundColor: '#f0f5f5',
                    padding: '5px 10px',
                    borderRadius: '5px',
                  }}
                >
                  {t('upload-pdf')}
                </div>
                <input type="file" accept="application/pdf" onChange={handleFileUpload} />
              </div>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                background: 'linear-gradient(to top left, #f0f0f5, #33adff55)',
                padding: '15px 20px 20px 20px',
                borderRadius: '5px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  marginTop: '1vh',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: 'bold',
                }}
              >
                Item {selectedCode ? selectedCode : selectedEquipment ? selectedEquipment : ''} does not have drawing to
                display.
              </div>
            </div>
          )}
        </Modal>

        <Modal
          title={`Uploading ${totalFileCount} Files for Consumables`}
          width={'27%'}
          visible={showUploadAllConsumablesModal}
          onCancel={() => handleUploadConsumableDrawingsModalCancel()}
          destroyOnClose
          footer={false}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'linear-gradient(to top left, #f0f0f5, #33adff55)',
              padding: '15px 20px 20px 20px',
              borderRadius: '5px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5vh',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <Progress percent={progressPercentage} type="circle" />
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '17px',
                  backgroundColor: '#f0f5f5',
                  padding: '5px 10px',
                  borderRadius: '5px',
                }}
              >
                {processedFileCount} out of {totalFileCount} uploaded
              </div>
            </div>
          </div>
        </Modal>

        <Modal
          title={
            selectedCode.trim().length > 0
              ? `${t('consumable')} : ${selectedCode}`
              : `EQUIPMENT : ${selectedEquipment} (${selectedEquipmentId})`
          }
          width={'75%'}
          visible={showConsumableCodeDrawing}
          onCancel={() => handleDrawingModalCancel()}
          footer={false}
          destroyOnClose
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: '2vh',
              marginLeft: '2vh',
              marginRight: '2vw',
              flex: '1',
              backgroundColor: '#cccccc99',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <PopConfirm
                title={<div>{t('delete-confirmation-text')} ?</div>}
                onConfirm={handleDeletePDF}
                okText={t('delete')}
                cancelText={t('cancel')}
              >
                <Button
                  style={{ width: '2vw' }}
                  prefixIcon={{ name: 'delete' }}
                  disabled={!(iFrameSrc.trim().length > 0) || !isAdmin}
                />
              </PopConfirm>
            </div>
            <iframe src={iFrameSrc} title="PDF" style={{ height: '68vh', width: '100%' }} />
          </div>
        </Modal>

        <Modal
          title={`UPDATE LIFETIME AND CYCLE FOR ${dataForTable.length} ITEMS`}
          visible={editlifeandcycle}
          okText={t('save')}
          onCancel={() => handleLifeCycleEditModalClose()}
          onOk={() => handleLifeCycleEditModalCSave()}
          destroyOnClose
        >
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', maxHeight: '10vh', overflowY: 'auto' }}>
              {dataForTable.map((tableItems, index) => {
                return (
                  <Tooltip
                    title={
                      <div style={{ margin: '4px' }}>
                        {Object.keys(tableItems).map((tableItem_key) => {
                          return (
                            <div
                              key={tableItem_key}
                              style={{ margin: '4px' }}
                            >{`${tableItem_key.toUpperCase()} : ${tableItems[tableItem_key]}`}</div>
                          );
                        })}
                      </div>
                    }
                    key={index + tableItems.consumablecode}
                  >
                    <div
                      key={tableItems.consumablecode}
                      style={{ margin: '2px', padding: '2px', border: '1px solid lightgrey', borderRadius: '5px' }}
                    >
                      {tableItems.consumablecode}
                    </div>
                  </Tooltip>
                );
              })}
            </div>

            <div
              style={{
                display: 'flex',
                backgroundColor: '#cccccc',
                padding: '2rem',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '5px',
                borderRadius: '5px',
              }}
            >
              <div>LIFETIME</div>
              <Input
                key="lifetime"
                value={globalLife}
                onChange={(e) => setGlobalLife(e.target.value)}
                type="number"
                placeholder="lifetime"
              />
              <div style={{ marginLeft: '5px' }}>CYCLE</div>
              <Input
                key="cycle"
                value={globalCycle}
                onChange={(e) => setGlobalCycle(e.target.value)}
                type="number"
                placeholder="cycle"
              />
            </div>
          </div>
        </Modal>
      </styled.ConsumableLifetimeCycle>
    </ThemeProvider>
  );
}

export default memo(ConsumableLifetimeCycle) as unknown as typeof ConsumableLifetimeCycle;
